import {
  PROJECT_NAME_MAX,
  projectSlugSchema,
  SLUG_MAX_LENGTH,
  SLUG_MIN_LENGTH,
  toSlug,
} from '@mogzo/shared';
import { type FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Page } from '../components/Page';
import { Alert, buttonClass, cardClass, fieldClass } from '../components/ui';
import { ApiError, apiGet, apiSend, type Project } from '../lib/api';

type SlugCheck =
  | { state: 'idle' | 'checking' }
  | { state: 'done'; available: boolean; message: string };

/** Asks the API whether an address is free, 300 ms after typing stops. */
function useSlugCheck(slug: string): SlugCheck {
  const [check, setCheck] = useState<SlugCheck>({ state: 'idle' });
  useEffect(() => {
    if (!slug) {
      setCheck({ state: 'idle' });
      return;
    }
    const local = projectSlugSchema.safeParse(slug);
    if (!local.success) {
      setCheck({
        state: 'done',
        available: false,
        message: local.error.issues[0]?.message ?? 'Not allowed',
      });
      return;
    }
    setCheck({ state: 'checking' });
    const controller = new AbortController();
    const timer = setTimeout(() => {
      apiGet<{ available: boolean; message: string }>(
        `/v1/dev/projects/slug-check?slug=${encodeURIComponent(slug)}`,
        controller.signal,
      )
        .then((result) => setCheck({ state: 'done', ...result }))
        .catch(() => {
          if (!controller.signal.aborted) setCheck({ state: 'idle' });
        });
    }, 300);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [slug]);
  return check;
}

export function NewProject() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugEdited, setSlugEdited] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const check = useSlugCheck(slug);
  const example = slug || 'your-game';

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!name.trim()) return setError('Enter the game’s name.');
    if (check.state === 'done' && !check.available) return setError(check.message);
    setBusy(true);
    setError(null);
    try {
      const project = await apiSend<Project>('POST', '/v1/dev/projects', {
        name: name.trim(),
        slug,
      });
      navigate(`/projects/${project.id}`, { state: { created: true } });
    } catch (failed) {
      setError(failed instanceof ApiError ? failed.message : 'Could not reach the API. Try again.');
      setBusy(false);
    }
  }

  return (
    <Page
      title="New project"
      description="One project per game."
      crumbs={[{ label: 'Projects', to: '/' }]}
    >
      <div className="flex flex-wrap items-start gap-6">
        <section
          aria-labelledby="new-heading"
          className={`${cardClass} min-w-0 flex-[2_1_480px] rounded-[8px] p-6 sm:p-8`}
        >
          <h2 id="new-heading" className="mz-panel-title">
            Name and address
          </h2>

          <form onSubmit={submit} noValidate className="mt-5 space-y-5">
            {error && <Alert tone="error">{error}</Alert>}
            <div className="space-y-1.5">
              <label htmlFor="project-name" className="block text-sm font-semibold">
                Game name
              </label>
              <input
                id="project-name"
                className={fieldClass}
                maxLength={PROJECT_NAME_MAX}
                value={name}
                onChange={(event) => {
                  setName(event.target.value);
                  if (!slugEdited) setSlug(toSlug(event.target.value));
                }}
              />
              <p className="text-[13px] text-muted">You can rename it any time.</p>
            </div>

            <div className="space-y-1.5">
              <label htmlFor="project-slug" className="block text-sm font-semibold">
                Address
              </label>
              <div className="flex items-stretch overflow-hidden rounded-[6px] border border-line bg-field focus-within:border-ink focus-within:bg-card">
                <span className="flex items-center pr-1 pl-3.5 font-mono text-sm text-muted">
                  mogzo.com/games/
                </span>
                <input
                  id="project-slug"
                  aria-describedby="slug-status slug-help"
                  className="min-h-11 min-w-0 flex-1 bg-transparent py-2.5 pr-3.5 font-mono text-sm text-ink focus:outline-none"
                  maxLength={SLUG_MAX_LENGTH}
                  autoCapitalize="off"
                  spellCheck={false}
                  value={slug}
                  onChange={(event) => {
                    setSlug(event.target.value.toLowerCase());
                    setSlugEdited(true);
                  }}
                />
              </div>
              <p
                id="slug-status"
                aria-live="polite"
                className={`min-h-5 text-[13px] font-semibold ${
                  check.state === 'done'
                    ? check.available
                      ? 'text-success'
                      : 'text-danger'
                    : 'text-muted'
                }`}
              >
                {check.state === 'checking'
                  ? 'Checking…'
                  : check.state === 'done'
                    ? check.message
                    : ''}
              </p>
              <p id="slug-help" className="text-[13px] text-muted">
                {SLUG_MIN_LENGTH}–{SLUG_MAX_LENGTH} lowercase letters, numbers and hyphens. It can’t
                be changed later, so links to your game keep working.
              </p>
            </div>

            <div className="flex flex-wrap gap-3 pt-1">
              <button
                type="submit"
                disabled={busy || (check.state === 'done' && !check.available)}
                className={buttonClass('primary')}
              >
                {busy ? 'Creating…' : 'Create project'}
              </button>
              <Link to="/" className={buttonClass('secondary')}>
                Cancel
              </Link>
            </div>
          </form>
        </section>

        <aside
          aria-labelledby="what-heading"
          className={`${cardClass} min-w-0 flex-[1_1_280px] rounded-[8px] px-7 py-6`}
        >
          <h2 id="what-heading" className="text-[15px] font-semibold">
            What a project gives you
          </h2>
          <ul className="mt-3.5 list-disc space-y-2.5 pl-5 text-sm leading-relaxed text-muted">
            <li>
              A private test community at{' '}
              <span className="font-mono text-ink">/test/{example}</span>, only for you and testers
              you invite.
            </li>
            <li>A place for builds you upload with the CLI.</li>
            <li>
              Its game page at <span className="font-mono text-ink">/games/{example}</span> once
              it’s reviewed and published.
            </li>
          </ul>
        </aside>
      </div>
    </Page>
  );
}
